import { cn } from '../lib/cn';

export interface StepRailProps {
  steps: string[];
  /** Zero-based index of the step being edited. */
  current: number;
  className?: string;
}

/**
 * The publish flow's progress rail. Completed and current steps both take a
 * filled ngahere dot; only the label weight distinguishes them, as in the
 * export.
 */
export function StepRail({ steps, current, className }: StepRailProps) {
  return (
    <ol className={cn('flex flex-col gap-4', className)}>
      {steps.map((label, index) => {
        const done = index < current;
        const active = index === current;

        return (
          <li
            key={label}
            aria-current={active ? 'step' : undefined}
            className={cn(
              'flex items-center gap-2 text-label',
              active && 'font-bold text-ngahere',
              done && 'font-medium text-ink-2',
              !active && !done && 'font-medium text-placeholder',
            )}
          >
            <span
              className={cn(
                'flex h-6 w-6 flex-none items-center justify-center rounded-full text-caption font-bold',
                done || active ? 'bg-ngahere text-white' : 'bg-line-soft text-placeholder',
              )}
            >
              {index + 1}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

export interface RouteTimelineProps {
  departure: { time: string; place: string; note?: string };
  arrival: { time: string; place: string; note?: string };
  className?: string;
}

/** Pickup → drop-off timeline on the trip detail screen. */
export function RouteTimeline({ departure, arrival, className }: RouteTimelineProps) {
  return (
    <div className={cn('flex gap-3.5', className)}>
      <div className="flex flex-col items-center pt-1" aria-hidden="true">
        <span className="h-[11px] w-[11px] rounded-full border-[3px] border-ngahere" />
        <span className="min-h-[34px] w-0.5 flex-1 bg-line-firm" />
        <span className="h-[11px] w-[11px] rounded-full bg-pohutukawa" />
      </div>
      <div className="flex flex-1 flex-col gap-[26px]">
        <div>
          <div className="text-body-lg font-bold">
            {departure.time} · {departure.place}
          </div>
          {departure.note ? <div className="text-meta text-subtle">{departure.note}</div> : null}
        </div>
        <div>
          <div className="text-body-lg font-bold">
            {arrival.time} · {arrival.place}
          </div>
          {arrival.note ? <div className="text-meta text-subtle">{arrival.note}</div> : null}
        </div>
      </div>
    </div>
  );
}

export interface RideTimeColumnProps {
  departure: string;
  arrival: string;
  duration: string;
  className?: string;
}

/** The compact departure/duration/arrival column on a search-result card. */
export function RideTimeColumn({ departure, arrival, duration, className }: RideTimeColumnProps) {
  return (
    <div className={cn('flex min-w-[96px] flex-col items-start', className)}>
      <span className="font-display text-title-sm font-bold">{departure}</span>
      <div className="my-[5px] flex items-center gap-1.5 text-timeline" aria-hidden="true">
        <span className="h-[7px] w-[7px] rounded-full border-2 border-ngahere" />
        <span className="h-[18px] w-0.5 bg-line-firm" />
      </div>
      <span className="-mt-1.5 text-mini text-subtle">{duration}</span>
      <div className="my-[5px] flex items-center gap-1.5" aria-hidden="true">
        <span className="h-[7px] w-[7px] rounded-full bg-pohutukawa" />
      </div>
      <span className="font-display text-title-sm font-bold">{arrival}</span>
    </div>
  );
}
